import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import AdminDrops from "@/components/AdminDrops";
import { Card, PageTitle, inputCls, labelCls, btnPrimary, btnGhost } from "@/admin/ui";

export default function Settings() {
  const [site, setSite] = useState(null);
  const [launchDate, setLaunchDate] = useState("");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [lookPct, setLookPct] = useState("10");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/settings").then((r) => {
    setSite(r.data);
    setLaunchDate((r.data.launch_date || "").slice(0, 10));
    setNotifyEmail(r.data.notify_email || "");
    setLookPct(String(r.data.look_discount_percent ?? 10));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveLookPct = async () => {
    const n = Number(lookPct);
    if (Number.isNaN(n) || n < 0 || n > 50) { toast.error("Enter a percentage between 0 and 50."); return; }
    setBusy(true);
    try {
      await api.put("/admin/settings", { look_discount_percent: n });
      toast.success(n === 0 ? "Complete Look discount disabled." : `Complete Look discount set to ${n}%.`);
      load();
    } catch { toast.error("Could not save."); }
    setBusy(false);
  };

  const saveNotifyEmail = async () => {
    if (!notifyEmail.includes("@")) { toast.error("Enter a valid email address."); return; }
    setBusy(true);
    try {
      await api.put("/admin/settings", { notify_email: notifyEmail.trim() });
      toast.success(`Order notifications will go to ${notifyEmail.trim()}.`);
      load();
    } catch { toast.error("Could not save."); }
    setBusy(false);
  };

  const saveDate = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", { launch_date: `${launchDate}T00:00:00` });
      toast.success("Launch date updated.");
      load();
    } catch { toast.error("Could not save."); }
    setBusy(false);
  };

  const launch = async () => {
    if (!window.confirm("LAUNCH THE SITE? This makes the full store public AND emails everyone on the launch list. This is the big one.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/launch");
      toast.success(`SYNC is LIVE — ${data.notified}/${data.attempted} launch emails sent.`);
      load();
    } catch { toast.error("Launch failed."); }
    setBusy(false);
  };

  const reenable = async () => {
    if (!window.confirm("Put the site back into Coming Soon mode? Visitors will only see the launch page.")) return;
    setBusy(true);
    try {
      await api.put("/admin/settings", { coming_soon: true });
      toast.success("Coming Soon mode re-enabled.");
      load();
    } catch { toast.error("Could not update."); }
    setBusy(false);
  };

  const toggleLowNotice = async () => {
    setBusy(true);
    try {
      const next = !(site?.low_stock_notice !== false);
      await api.put("/admin/settings", { low_stock_notice: next });
      toast.success(next ? "Shoppers will see 'Only X left' on low-stock sizes." : "'Only X left' notices hidden.");
      load();
    } catch { toast.error("Could not update."); }
    setBusy(false);
  };

  return (
    <div data-testid="admin-settings-page">
      <PageTitle title="Settings" sub="Site mode, launch controls, drops and integrations." />

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-6" data-testid="settings-site-mode">
          <p className={labelCls}>Site mode</p>
          <p className="font-anton text-2xl mb-1">
            {site?.coming_soon ? <span className="text-signal">COMING SOON</span> : <span className="text-pulse">LIVE</span>}
          </p>
          <p className="text-graphite text-xs leading-relaxed mb-5">
            {site?.coming_soon
              ? "Visitors see the launch page + story only. You (admin) see the full store."
              : "The full store is public."}
          </p>
          <div className="flex flex-wrap items-end gap-3 mb-5">
            <div>
              <label className={labelCls}>Launch date (countdown)</label>
              <input type="date" value={launchDate} onChange={(e) => setLaunchDate(e.target.value)}
                className={`${inputCls} bg-ink`} data-testid="settings-launch-date" />
            </div>
            <button onClick={saveDate} disabled={busy || !launchDate} className={btnGhost} data-testid="settings-save-date">Save Date</button>
          </div>
          {site?.coming_soon ? (
            <button onClick={launch} disabled={busy} className={btnPrimary} data-testid="settings-launch-btn">
              🚀 Launch Site + Email Launch List
            </button>
          ) : (
            <button onClick={reenable} disabled={busy} className={btnGhost} data-testid="settings-reenable-btn">
              Re-enable Coming Soon Mode
            </button>
          )}
        </Card>

        <Card className="p-6" data-testid="settings-tapstitch">
          <p className={labelCls}>Tapstitch integration</p>
          <p className="font-anton text-2xl mb-1">Manual Fulfillment</p>
          <div className="text-graphite text-xs leading-relaxed space-y-2">
            <p>
              Tapstitch does <span className="text-chalk">not offer a public API</span> — their official integrations
              are store connectors for Shopify, WooCommerce, Etsy, Wix, Squarespace and TikTok Shop only.
              A direct automatic order feed from this custom site is therefore not possible today.
            </p>
            <p className="text-chalk">Current workflow (built in):</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Customer pays on the site — order appears under Orders as "New".</li>
              <li>Open the order → "Copy Fulfillment Summary" → paste into a manual Tapstitch order.</li>
              <li>Set the order to In Production. When Tapstitch ships it, add the tracking number and mark Shipped — the customer gets a tracking email automatically.</li>
            </ol>
            <p>
              The system is adapter-ready: each order stores a fulfillment provider, sent-at timestamp and tracking —
              if Tapstitch releases an API (or you add another partner), automation plugs into the same fields.
            </p>
          </div>
          <a href="https://www.tapstitch.com" target="_blank" rel="noopener noreferrer"
            className={`${btnGhost} inline-flex mt-4`} data-testid="settings-tapstitch-link">Open Tapstitch ↗</a>
        </Card>

        <Card className="p-6" data-testid="settings-order-notify">
          <p className={labelCls}>Order notifications</p>
          <p className="font-anton text-2xl mb-1">New Order Emails</p>
          <p className="text-graphite text-xs leading-relaxed mb-5">
            Every time a Stripe payment is confirmed, an email with the order details and the customer's
            shipping address is sent to this address instantly.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className={labelCls}>Notification email</label>
              <input type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="info@thesyncwear.com" className={inputCls} data-testid="settings-notify-email" />
            </div>
            <button onClick={saveNotifyEmail} disabled={busy} className={btnPrimary} data-testid="settings-notify-save">Save</button>
          </div>
          <p className="font-mono text-[10px] text-graphite mt-3">
            Defaults to info@thesyncwear.com when empty.
          </p>
        </Card>

        <Card className="p-6" data-testid="settings-look-discount">
          <p className={labelCls}>Bundle discount</p>
          <p className="font-anton text-2xl mb-1">Complete Look Discount</p>
          <p className="text-graphite text-xs leading-relaxed mb-5">
            Applied automatically at checkout when the bag has a top + a bottom together.
            Set 0 to disable. Note: while this discount is active on an order, the promo-code field
            is hidden at checkout (Stripe rule).
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className={labelCls}>Discount (%)</label>
              <input type="number" min="0" max="50" step="1" value={lookPct}
                onChange={(e) => setLookPct(e.target.value)}
                className={`${inputCls} w-28`} data-testid="settings-look-pct" />
            </div>
            <button onClick={saveLookPct} disabled={busy} className={btnPrimary} data-testid="settings-look-save">Save</button>
          </div>
          <p className="font-mono text-[10px] text-graphite mt-3">
            Currently: {site?.look_discount_percent === 0 ? "disabled" : `${site?.look_discount_percent ?? 10}% off full looks`}.
          </p>
        </Card>

        <Card className="p-6" data-testid="settings-low-stock">
          <p className={labelCls}>Shopper notices</p>
          <p className="font-anton text-2xl mb-1">"Only X Left"</p>
          <p className="text-graphite text-xs leading-relaxed mb-5">
            When a tracked size has 5 or fewer left, shoppers see an "Only X left" note on the product page.
            Turn it off to hide stock levels from customers.
          </p>
          <label className="flex items-center gap-3 font-mono text-xs uppercase tracking-wider cursor-pointer">
            <input type="checkbox" checked={site?.low_stock_notice !== false} onChange={toggleLowNotice}
              disabled={busy} data-testid="settings-low-stock-toggle" style={{ accentColor: "rgb(var(--signal))" }} />
            {site?.low_stock_notice !== false ? "Enabled — shoppers see low-stock notes" : "Disabled — hidden from shoppers"}
          </label>
        </Card>
      </div>

      <div className="mt-8" data-testid="settings-drops">
        <AdminDrops />
      </div>
    </div>
  );
}
